import _defineProperty from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["styles", "collapsible", "hasIcon", "isOpen", "headerIcon", "controlled", "requestToggle", "eventKey", "easing", "triggerTagName", "unmountOnExit", "children", "triggerRenderer", "contentRenderer", "triggerIconReversed", "isInitiallyOpen", "trigger", "onToggle", "onOpening", "onClosing", "onOpen", "onClose", "rendererPropNames"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import classNames from 'classnames';
import _isNil from 'lodash/isNil.js';
import _pick from 'lodash/pick.js';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { applyStylingHoc } from '../../lib/styling.js';
import BaseCollapsible from './TsboCollapsible.js';
import baseStyles from './Collapsible.scss';

var thisVoid = function thisVoid() {
  return null;
};

export var CollapsibleContext = createContext({
  closeCollapsible: thisVoid
});
export var useCollapsible = function useCollapsible() {
  return useContext(CollapsibleContext);
};
var EMPTY_OBJECT = {};
var defaultEasing = 'cubic-bezier(0.23, 1, 0.32, 1)';
var defaultTriggerTagName = 'div';
/**
 * Collapsible component
 */

export var Collapsible = function Collapsible(props) {
  var _props$styles = props.styles,
      styles = _props$styles === void 0 ? EMPTY_OBJECT : _props$styles,
      _props$collapsible = props.collapsible,
      collapsible = _props$collapsible === void 0 ? true : _props$collapsible,
      _props$hasIcon = props.hasIcon,
      hasIcon = _props$hasIcon === void 0 ? true : _props$hasIcon,
      isOpen = props.isOpen,
      headerIcon = props.headerIcon,
      controlled = props.controlled,
      requestToggle = props.requestToggle,
      eventKey = props.eventKey,
      _props$easing = props.easing,
      easing = _props$easing === void 0 ? defaultEasing : _props$easing,
      _props$triggerTagName = props.triggerTagName,
      triggerTagName = _props$triggerTagName === void 0 ? defaultTriggerTagName : _props$triggerTagName,
      unmountOnExit = props.unmountOnExit,
      children = props.children,
      triggerRenderer = props.triggerRenderer,
      contentRenderer = props.contentRenderer,
      _props$triggerIconRev = props.triggerIconReversed,
      triggerIconReversed = _props$triggerIconRev === void 0 ? false : _props$triggerIconRev,
      _props$isInitiallyOpe = props.isInitiallyOpen,
      isInitiallyOpen = _props$isInitiallyOpe === void 0 ? true : _props$isInitiallyOpe,
      trigger = props.trigger,
      onToggle = props.onToggle,
      onOpening = props.onOpening,
      onClosing = props.onClosing,
      onOpen = props.onOpen,
      onClose = props.onClose,
      rendererPropNames = props.rendererPropNames,
      rest = _objectWithoutProperties(props, _excluded);

  var _useState = useState({
    prevIsOpen: isOpen,
    // $FlowFixMe: isNil guarantees a boolean here
    isOpen: !_isNil(isOpen) ? isOpen : isInitiallyOpen,
    isOpening: false,
    isClosing: false,
    animatingClose: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var isOpening = state.isOpening,
      isClosing = state.isClosing,
      prevIsOpen = state.prevIsOpen,
      stateIsOpen = state.isOpen,
      animatingClose = state.animatingClose;
  useEffect(function () {
    if (isOpening || isClosing) {
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          prevIsOpen: !isOpen
        });
      });
    } else if (!_isNil(isOpen) && isOpen !== prevIsOpen) {
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          prevIsOpen: isOpen,
          isOpen: isOpen,
          animatingClose: state.isOpen
        });
      });
    }
  }, [isOpening, isClosing, prevIsOpen, isOpen]);
  var renderer = useMemo(function () {
    return typeof children === 'function' ? children : contentRenderer;
  }, [children, contentRenderer]);
  var rendererProps = useMemo(function () {
    return rendererPropNames ? _pick(props, rendererPropNames) : EMPTY_OBJECT;
  }, [props, rendererPropNames]);
  var handleToggle = useCallback(function () {
    if (requestToggle && !requestToggle()) {
      return;
    }

    if (controlled && onToggle) {
      return onToggle(eventKey, !stateIsOpen);
    }

    if (collapsible && !isOpening && !isClosing) {
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          isOpen: !state.isOpen,
          animatingClose: true
        });
      });
      onToggle && onToggle(eventKey, !stateIsOpen);
    }
  }, [requestToggle, controlled, collapsible, isOpening, isClosing, onToggle, eventKey, stateIsOpen]);
  var handleOpening = useCallback(function () {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        isOpening: true
      });
    });
    onOpening && onOpening(eventKey);
  }, [eventKey, onOpening]);
  var handleOpen = useCallback(function () {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        isOpening: false
      });
    });
    onOpen && onOpen(eventKey);
  }, [eventKey, onOpen]);
  var handleClosing = useCallback(function () {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        isClosing: true
      });
    });
    onClosing && onClosing(eventKey);
  }, [eventKey, onClosing]);
  var handleClose = useCallback(function () {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        isClosing: false,
        animatingClose: false
      });
    });
    onClose && onClose(eventKey);
  }, [eventKey, onClose]);
  var thisTrigger = useMemo(function () {
    return React.createElement("span", {
      className: styles.header
    }, headerIcon && React.createElement("span", {
      className: classNames(styles.headerIcon, headerIcon)
    }), trigger);
  }, [headerIcon, styles.header, styles.headerIcon, trigger]); // Context

  var closeCollapsible = useCallback(function () {
    if (requestToggle && !requestToggle()) {
      return;
    }

    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        isOpen: false,
        animatingClose: true
      });
    });
    onToggle && onToggle(eventKey, false);
  }, [requestToggle, onToggle, eventKey]);
  var contextValue = useMemo(function () {
    return {
      closeCollapsible: closeCollapsible
    };
  }, [closeCollapsible]);
  var renderCollapsible = useMemo(function () {
    return (// @ts-ignore
      React.createElement(BaseCollapsible, _objectSpread({
        className: classNames(styles.root, hasIcon && styles['has-icon'], isOpening && styles['is-opening'], isClosing && styles['is-closing'], !collapsible && styles['is-static']),
        openedClassName: classNames(styles.root, styles['is-open'], hasIcon && styles['has-icon'], isOpening && styles['is-opening'], isClosing && styles['is-closing'], !collapsible && styles['is-static']),
        triggerClassName: classNames(styles.trigger, hasIcon && styles['has-icon'], triggerIconReversed && styles['is-icon-reversed']),
        triggerOpenedClassName: classNames(styles.trigger, hasIcon && styles['has-icon'], triggerIconReversed && styles['is-icon-reversed']),
        contentOuterClassName: styles.content,
        contentInnerClassName: styles.innerContent,
        handleTriggerClick: handleToggle,
        open: stateIsOpen,
        lazyRender: unmountOnExit,
        trigger: triggerRenderer ? triggerRenderer(stateIsOpen) : thisTrigger,
        accordionPosition: eventKey,
        onOpening: handleOpening,
        onOpen: handleOpen,
        onClosing: handleClosing,
        onClose: handleClose,
        easing: easing,
        triggerTagName: triggerTagName,
        transitionTime: 500,
        transitionCloseTime: 500
      }, rest), (unmountOnExit && (stateIsOpen || isClosing || animatingClose) || !unmountOnExit) && (renderer ? renderer(stateIsOpen || isClosing || animatingClose, // $FlowFixMe: isNil guarantees a boolean here
      {
        isOpen: !_isNil(isOpen) ? isOpen : stateIsOpen,
        isClosing: isClosing,
        animatingClose: animatingClose,
        eventKey: eventKey,
        rendererProps: rendererProps
      }) : children))
    );
  }, [animatingClose, children, collapsible, easing, eventKey, handleClose, handleClosing, handleOpen, handleOpening, handleToggle, hasIcon, isClosing, isOpening, renderer, rest, stateIsOpen, styles, thisTrigger, triggerIconReversed, triggerRenderer, triggerTagName, unmountOnExit, rendererProps, isOpen]);
  return React.createElement(CollapsibleContext.Provider, {
    value: contextValue
  }, renderCollapsible);
};
export default applyStylingHoc(baseStyles)(Collapsible);